import { useTranslation } from 'react-i18next'

import { Box, Text } from '@enechain/ecloud-designsystem'

import { IntradayBulkResultBanner } from '~/pages/IntradayBidding/components/IntradayBulkResultBanner'
import type { IntradayBulkWithdrawRefused } from '~/pages/IntradayBidding/hooks/useIntradayBulkWithdraw'
import { intradayBulkWithdrawRefusalCounts } from '~/pages/IntradayBidding/models/intradayBulkWithdrawTargets'
import { formatWithdrawSummary } from '~/pages/IntradayBidding/models/withdrawSummary'

const KEY = 'page:biddingIntraday.bulkWithdraw.refused'

type Props = {
  refused: IntradayBulkWithdrawRefused
  onDismiss: () => void
}

/**
 * 一括板取消で受け付けなかった札があった時のエラーバナー (AC-BIN-112)。 見出しは受け付けた件数
 * (0 件なら見出しを出さず本文だけ)、 本文は理由ごとの件数 (GC → 照合 → 状態の順)。 「詳細を見る」 の
 * ダイアログに札の概要 (AC-BIN-008) と理由を並べる。 バナーの形は `IntradayBulkResultBanner`。
 */
export const IntradayBulkWithdrawRefusedBanner = ({
  refused,
  onDismiss,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const counts = intradayBulkWithdrawRefusalCounts(refused.bids)
  return (
    <IntradayBulkResultBanner
      detailsTitle={t(`${KEY}.detailsTitle`)}
      lines={counts.map(({ reason, count }) =>
        t(`${KEY}.count.${reason}`, { count }),
      )}
      onDismiss={onDismiss}
      title={
        refused.acceptedCount > 0
          ? t(`${KEY}.title`, { count: refused.acceptedCount })
          : undefined
      }
    >
      <Box as="ul" fontVariantNumeric="tabular-nums" listStyleType="none">
        {refused.bids.map(({ bid, reason }) => (
          <Box key={bid.id} as="li" paddingY={1}>
            <Text>
              {formatWithdrawSummary(t, {
                timeCode: bid.timeCode,
                areaCode: bid.areaCode,
                direction: bid.direction,
                orderMethod: bid.orderMethod,
                priceYenPerKwh: bid.priceYenPerKwh,
                // 行の volumeMw は MW 保持 (AC-BIN-043)。 概要も MW で出すので換算しない。
                volumeMw: bid.volumeMw,
              })}
            </Text>
            <Text textStyle="captionNormal" color="Text.midEmphasis">
              {t(`${KEY}.reason.${reason}`)}
            </Text>
          </Box>
        ))}
      </Box>
    </IntradayBulkResultBanner>
  )
}
